import {
  cycleDayToday,
  latestLuteal,
  lutealOf,
  painStats,
  previousWindowAverage,
  windowItems,
} from './context';
import { NO_LUTEAL_NOTE } from './metrics/windowMetrics';
import { addDays, fm, fr } from './dates';
import { capitalize, cardDelta, lowLog, mean, num, pl, r1 } from './format';
import { PHASE_STYLE } from './palette';

// The four summary cards. Their state (today / before / during / after a
// period) is derived from the data — the design's `cardState` prop showed the
// four looks, cycleModel.cardState picks one.

const TAG = ['#F4F0EC', '#3a3a3a'];
const FINAL = ['#1f1f1f', '#fff'];
const MUTED = '#6b6b6b';

function card(id, name, color, tag, tagColors, value, unit, l3, l4, warn) {
  return {
    id,
    name,
    color,
    tag,
    hasTag: Boolean(tag),
    tagBg: tagColors[0],
    tagFg: tagColors[1],
    value,
    unit,
    l3: l3[0],
    l3fg: l3[1],
    l4,
    warn: warn || '',
    hasWarn: Boolean(warn),
  };
}

function cycleCard(ctx) {
  const name = ['periods', 'Cycle', '#D49073'];
  if (!ctx.cur) return card(...name, '', TAG, '—', '', ['No period logged yet', MUTED], '');
  const cd = cycleDayToday(ctx);
  const next = ctx.nextPeriod;
  const lp = ctx.latestPeriod;
  const phaseLine = () => {
    const key = ctx.info(ctx.today)?.phaseKey;
    const phase = ctx.period.phase ? capitalize(ctx.period.phase) : null;
    if (!phase) return ['Phase unavailable', MUTED];
    return [`${phase} phase · estimated`, (PHASE_STYLE[key] || PHASE_STYLE[phase] || PHASE_STYLE.Luteal).fg];
  };

  if (ctx.state === 'during') {
    const today = ctx.logs.period[ctx.today];
    const flow = today?.flow ? `${today.flow} flow` : 'flow not recorded';
    const s = painStats(ctx, ctx.cur.start, ctx.today);
    const todayPain = today?.pain;
    const parts = [];
    if (todayPain != null) parts.push(`Pain today ${num(todayPain)}/5`);
    if (s.hi) parts.push(`highest so far ${num(s.hi.v)} (day ${ctx.info(s.hi.date)?.cd})`);
    return card(...name, 'Period ongoing', ['#B5654A', '#fff'], `Day ${cd}`, '', [`Period day ${cd} · ${flow}`, '#9A4F37'],
      parts.length ? capitalize(parts.join(' · ')) : 'No pain logged yet this period');
  }

  if (ctx.state === 'before' && next) {
    const d = next.days_until;
    const l4 = d > 0 ? `Period expected around ${fm(next.date)} · in ~${pl(d, 'day')}`
      : d === 0 ? `Period expected today (${fm(next.date)})`
        : `Period expected ${fm(next.date)} · ${pl(-d, 'day')} late`;
    return card(...name, '', TAG, `Day ${cd}`, '', phaseLine(), l4);
  }

  const parts = [];
  if (lp) {
    parts.push(`Last period ${fr(lp.start_date, lp.end_date)} (${pl(lp.period_length, 'day')})`);
    if (lp.avg_pain != null) parts.push(`avg pain ${r1(lp.avg_pain)}/5`);
  }
  if (next?.date) parts.push(`next ~${fm(next.date)}`);
  return card(...name, '', TAG, `Day ${cd}`, '', phaseLine(), parts.join(' · '));
}

/** PMS and Cravings: the luteal phase (estimated), in place of the pre-period window (README gap G2). */
function lutealCard(ctx, metric) {
  const isPms = metric === 'pms';
  const name = isPms ? ['pms', 'PMS', '#B5654A'] : ['cravings', 'Cravings', '#F26A1F'];
  const topLine = (w) => {
    const items = windowItems(w);
    if (!items.length) return '';
    if (isPms) {
      const top = items.slice().sort((a, b) => (b.average ?? 0) - (a.average ?? 0))[0];
      return `Top symptom: ${top.name}`;
    }
    const top = items.slice().sort((a, b) => b.days - a.days || (b.average ?? 0) - (a.average ?? 0))[0];
    return `Mostly ${top.name}`;
  };
  const found = latestLuteal(ctx, metric);

  // The current cycle's luteal days so far, once they have begun.
  const live = lutealOf(ctx, metric, ctx.cur);
  if (live) {
    return card(...name, 'Provisional', TAG, r1(live.average), `/5 · luteal ${fr(live.start_date, live.end_date)}`,
      cardDelta(live.average, found?.w.average, true, 'vs last luteal phase'), topLine(live),
      lowLog(live.days_logged, live.days_total));
  }

  if (!found) return card(...name, '', TAG, '—', '', [NO_LUTEAL_NOTE[metric], MUTED], '');
  const { w } = found;
  const during = ctx.state === 'during';
  const nextStart = ctx.cur?.phases.find((p) => p.phase === 'luteal')?.start_date;
  return card(...name, during ? 'Final' : 'Last luteal phase', during ? FINAL : TAG, r1(w.average),
    `/5 · ${fr(w.start_date, w.end_date)} (est.)`,
    cardDelta(w.average, previousWindowAverage(found), true, 'vs last cycle'),
    during || !nextStart ? topLine(w) : `Next luteal phase from ~${fm(nextStart)}`,
    lowLog(w.days_logged, w.days_total));
}

function energyCard(ctx) {
  const name = ['energy', 'Energy', '#F26A1F'];
  const e = ctx.cur ? ctx.analytics.energy[ctx.cur.start] : null;
  if (!e) return card(...name, 'Live', TAG, '—', '', ['No energy logged this cycle', MUTED], '');
  const cd = e.cycle_day ?? cycleDayToday(ctx);
  let l4;
  if (ctx.state === 'during') l4 = `New cycle · ${e.days_logged} of ${cd} days logged`;
  else if (ctx.state === 'before') {
    const last4 = [0, 1, 2, 3].map((i) => ctx.logs.energy[addDays(ctx.today, -i)]);
    const a = mean(last4);
    l4 = a == null ? 'No entries in the last 4 days' : `Last 4 days avg ${r1(a)}/5`;
  } else {
    const m = e.by_phase?.menstrual?.average;
    l4 = m == null ? '' : `During period ${r1(m)}/5`;
  }
  return card(...name, 'Live', TAG, r1(e.average), `/5 · days 1–${cd}`,
    cardDelta(e.average, e.comparison?.previous_average, false, 'vs same days last cycle'), l4,
    lowLog(e.days_logged ?? 0, cd));
}

export function buildCards(ctx) {
  return [cycleCard(ctx), lutealCard(ctx, 'pms'), energyCard(ctx), lutealCard(ctx, 'cravings')];
}
